"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LookupTable = void 0;
const lookupTableGenerators_1 = require("./lookupTableGenerators");
class LookupTable {
    constructor(translation) {
        this.tableReference = {};
        this.tableId = {};
        this.tableReference = (0, lookupTableGenerators_1.generateTableReference)(translation);
        this.tableId = (0, lookupTableGenerators_1.generateTableId)(translation);
    }
    /**
     * Returns a flattened array of all the videos in this BBB translation.
     *
     * @returns An array of BBBVideo objects.
     */
    videos() {
        return Object.values(this.tableId);
    }
    /**
     * Returns an array of BBBVideo objects in the given book and chapter.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the book. Example: 1
     * @returns An array of BBBVideo objects.
     */
    videosInChapter(book, chapter) {
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        return Array.from(Object.values(this.tableReference[book][chapter]).reduce((acc, video) => acc.add(video), new Set()));
    }
    /**
     * Returns an array of available chapter numbers in the given book.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns An array of chapter strings.
     */
    chapterNumbers(book) {
        return Object.keys(this.tableReference[book]);
    }
    /**
     * Returns an object containing all the chapters in the given book.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns A Book object containing the chapters.
     */
    chapters(book) {
        if (!this.bookExists(book)) {
            return undefined;
        }
        return this.tableReference[book];
    }
    /**
     * Returns an array of available verse numbers in the given book and chapter.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the book. Example: 1
     * @returns An array of verse strings.
     */
    verseNumbers(book, chapter) {
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        return Object.keys(this.tableReference[book][chapter]);
    }
    nextChapterTriplet(book, chapter) {
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        const numChapters = this.numChapters(book);
        const chapterIndex = Object.keys(this.tableReference[book]).indexOf(chapter);
        if (chapterIndex === numChapters - 1) {
            return this.nextBook(book);
        }
        const nextChapter = Object.keys(this.tableReference[book])[chapterIndex + 1];
        return this.firstTripletInChapter(book, nextChapter);
    }
    prevChapterTriplet(book, chapter) {
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        const chapterIndex = Object.keys(this.tableReference[book]).indexOf(chapter);
        if (chapterIndex === 0) {
            return this.prevBook(book);
        }
        const prevChapter = Object.keys(this.tableReference[book])[chapterIndex - 1];
        return this.firstTripletInChapter(book, prevChapter);
    }
    nextBook(book) {
        if (!this.bookExists(book)) {
            return undefined;
        }
        const numBooks = this.numBooks();
        const bookIndex = Object.keys(this.tableReference).indexOf(book);
        const nextBook = Object.keys(this.tableReference)[(bookIndex + 1) % numBooks];
        const firstChapter = this.firstChapterNumber(nextBook);
        const firstVerse = this.firstVerseNumber(nextBook, firstChapter);
        return [nextBook, firstChapter, firstVerse];
    }
    /**
     *
     * @param book
     * @returns
     */
    prevBook(book) {
        if (!this.bookExists(book)) {
            return undefined;
        }
        const numBooks = this.numBooks();
        const bookIndex = Object.keys(this.tableReference).indexOf(book);
        const prevBook = Object.keys(this.tableReference)[(numBooks + bookIndex - 1) % numBooks];
        const firstChapter = this.firstChapterNumber(prevBook);
        const firstVerse = this.firstVerseNumber(prevBook, firstChapter);
        return [prevBook, firstChapter, firstVerse];
    }
    /**
     * Returns the first triplet in the translation.
     *
     * @returns The first triplet [book, chapter, verse] in the translation.
     */
    firstTripletInTranslation() {
        const firstBook = Object.keys(this.tableReference)[0];
        const firstChapter = Object.keys(this.tableReference[firstBook])[0];
        const firstVerse = Object.keys(this.tableReference[firstBook][firstChapter])[0];
        return [firstBook, firstChapter, firstVerse];
    }
    /**
     * Returns the first video in the given book.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns The BBBVideo object corresponding to the first video in the book, or undefined if the book does not exist.
     */
    firstTripletInBook(book) {
        const firstChapter = Object.keys(this.tableReference[book])[0];
        const firstVerse = Object.keys(this.tableReference[book][firstChapter])[0];
        return [book, firstChapter, firstVerse];
    }
    /**
     * Returns the first video in the given book and chapter.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the book. Example: 1
     * @returns The first video in the chapter, or undefined if the chapter does not exist.
     */
    firstTripletInChapter(book, chapter) {
        const verse = Object.keys(this.tableReference[book][chapter])[0];
        return [book, chapter, verse];
    }
    /**
     * Checks if a verse exists in the lookup table.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the verse. Example: 1
     * @param verse The verse. Example: 1. Also supports letters as subverses. Example: 2b
     *
     * @returns True if the verse exists, false otherwise.
     */
    verseExists(book, chapter, verse) {
        return this.lookupByReference(book, chapter, verse) !== undefined;
    }
    verseNumberFallback(book, chapter, verse) {
        const verseNumber = parseInt(verse);
        const verseAlphabet = verse.slice(-1);
        if (this.tableReference[book][chapter][verseNumber + verseAlphabet]) {
            return verseNumber + verseAlphabet;
        }
        return verseNumber.toString();
    }
    /**
     * Checks if a book exists in the lookup table.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns True if the book exists, false otherwise.
     */
    bookExists(book) {
        return this.tableReference[book] !== undefined;
    }
    /**
     * Checks if a chapter exists in the lookup table.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the book. Example: 1
     * @returns True if the chapter exists, false otherwise.
     */
    chapterExists(book, chapter) {
        if (!this.bookExists(book)) {
            return false;
        }
        return this.tableReference[book][chapter] !== undefined;
    }
    /**
     * Looks up a BBBVideo by its ID.
     * @param id The ID of the video.
     *
     * @returns The BBBVideo object corresponding to the ID, or undefined if not found.
     */
    lookupById(id) {
        return this.tableId[id];
    }
    /**
     * Validates a reference and returns the first available reference triplet if the reference is not found.
     *
     * @param book The shortcode of book. Example: GEN
     * @param chapter The chapter of the video. Example: 1
     * @param verse The verse of the video. Example: 1. Also supports letters as subverses. Example: 2b
     *
     * @returns The reference triplet if the reference is valid, or the first available reference triplet if the reference is not found.
     */
    validateReference(book, chapter, verse) {
        if (!this.bookExists(book)) {
            return this.firstTripletInTranslation();
        }
        if (!this.chapterExists(book, chapter)) {
            return this.firstTripletInBook(book);
        }
        if (!this.verseExists(book, chapter, verse)) {
            return this.firstTripletInChapter(book, chapter);
        }
        return [book, chapter, verse];
    }
    /**
     * Looks up a BBBVideo by its reference.
     *
     * @param book The shortcode of book. Example: GEN
     * @param chapter The chapter of the video. Example: 1
     * @param verse The verse of the video. Example: 1. Also supports letters as subverses. Example: 2b
     *
     * @returns The BBBVideo object corresponding to the reference, or undefined if not found.
     */
    lookupByReference(book, chapter, verse) {
        var _a;
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        const hasAlphabet = /[a-zA-Z]$/.test(verse);
        if (hasAlphabet) {
            const verseNumber = parseInt(verse.slice(0, -1));
            const verseAlphabet = verse.slice(-1);
            return ((_a = this.tableReference[book][chapter][verseNumber + verseAlphabet]) !== null && _a !== void 0 ? _a : this.tableReference[book][chapter][verseNumber]);
        }
        return this.tableReference[book][chapter][verse];
    }
    /**
     * Returns the first chapter number in the given book.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns The first chapter in the book, or undefined if the book does not exist.
     */
    firstChapterNumber(book) {
        return Object.keys(this.tableReference[book])[0];
    }
    /**
     * Returns the first verse number in the given book and chapter.
     *
     * @param book The shortcode of the book. Example: GEN
     * @param chapter The chapter of the book. Example: 1
     * @returns The verse number. Example: 1
     */
    firstVerseNumber(book, chapter) {
        if (!this.chapterExists(book, chapter)) {
            return undefined;
        }
        return Object.keys(this.tableReference[book][chapter])[0];
    }
    *chapterVerseIterator(book) {
        if (this.bookExists(book)) {
            const chapters = this.chapterNumbers(book);
            for (let chapter in chapters) {
                const verses = this.verseNumbers(book, chapter);
                for (let verse in verses) {
                    yield [chapter, verse];
                }
            }
        }
    }
    /**
     * Returns the number of books in the translation.
     *
     * @returns The number of books.
     */
    numBooks() {
        return Object.keys(this.tableReference).length;
    }
    /**
     * Returns the number of chapters in the given book. Assumes the book is valid.
     *
     * @param book The shortcode of the book. Example: GEN
     * @returns The number of chapters.
     */
    numChapters(book) {
        return Object.keys(this.tableReference[book]).length;
    }
}
exports.LookupTable = LookupTable;
